import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchCategorySalesReport, fetchCategorySalesProducts } from '../services/api';
import { ArrowUp, ArrowDown, RefreshCw, ChevronLeft } from 'lucide-react';

// ── Date helpers ──────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseIso = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); };
const monthName = (d) => d.toLocaleString('en-US', { month: 'short' });
const longMonth = (d) => d.toLocaleString('en-US', { month: 'long' });
const fmtShort = (d) => `${monthName(d)} ${d.getDate()}, ${d.getFullYear()}`;
const fmt$ = (n, opts = {}) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  if (opts.compact && abs >= 1000) {
    return `$${(v / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`;
  }
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};
const fmtPct = (p) => {
  if (p === null || p === undefined) return '—';
  const sign = p > 0 ? '+' : '';
  return `${sign}${p.toFixed(p >= 100 || p <= -100 ? 0 : 1)}%`;
};

// Shift a date back N calendar years, preserving month/day (Feb 29 → Feb 28).
function shiftYear(d, years = -1) {
  const result = new Date(d);
  result.setFullYear(result.getFullYear() + years);
  return result;
}

// Build current + prior date ranges from a preset + comparison choice.
function buildRange(preset, comparison, customStart, customEnd) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  let curStart, curEnd, curLabel;

  switch (preset) {
    case 'mtd': {
      curStart = new Date(today.getFullYear(), today.getMonth(), 1);
      curEnd = today;
      curLabel = `${longMonth(today)} ${today.getFullYear()} MTD`;
      break;
    }
    case 'ytd': {
      curStart = new Date(today.getFullYear(), 0, 1);
      curEnd = today;
      curLabel = `${today.getFullYear()} YTD`;
      break;
    }
    case 'this-week': {
      const dow = today.getDay();
      curStart = new Date(today);
      curStart.setDate(today.getDate() - dow); // Sunday
      curEnd = today;
      curLabel = `Week of ${fmtShort(curStart)}`;
      break;
    }
    case 'last-12-months': {
      curStart = new Date(today);
      curStart.setDate(today.getDate() - 364);
      curEnd = today;
      curLabel = 'Last 12 Months';
      break;
    }
    case 'last-month': {
      curStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      curEnd = new Date(today.getFullYear(), today.getMonth(), 0);
      curLabel = `${longMonth(curStart)} ${curStart.getFullYear()}`;
      break;
    }
    case 'custom':
    default: {
      curStart = customStart ? parseIso(customStart) : new Date(today.getFullYear(), today.getMonth(), 1);
      curEnd = customEnd ? parseIso(customEnd) : today;
      curLabel = `${fmtShort(curStart)} → ${fmtShort(curEnd)}`;
    }
  }

  let priStart, priEnd, priLabel;
  if (comparison === 'mom') {
    if (preset === 'mtd') {
      // Per spec: current MTD vs full prior month.
      priStart = new Date(curStart.getFullYear(), curStart.getMonth() - 1, 1);
      priEnd = new Date(curStart.getFullYear(), curStart.getMonth(), 0);
      priLabel = `${longMonth(priStart)} ${priStart.getFullYear()} (full)`;
    } else {
      priStart = new Date(curStart.getFullYear(), curStart.getMonth() - 1, curStart.getDate());
      priEnd = new Date(curEnd.getFullYear(), curEnd.getMonth() - 1, curEnd.getDate());
      priLabel = `Prior month`;
    }
  } else {
    // YoY — use DOW-aligned 364-day shift for week-grain so weekday traffic
    // patterns line up; otherwise calendar-year shift for clean labels.
    if (preset === 'this-week') {
      priStart = new Date(curStart); priStart.setDate(priStart.getDate() - 364);
      priEnd = new Date(curEnd); priEnd.setDate(priEnd.getDate() - 364);
    } else {
      priStart = shiftYear(curStart, -1);
      priEnd = shiftYear(curEnd, -1);
    }
    priLabel = `vs ${priStart.getFullYear()}`;
    if (preset === 'mtd' || preset === 'last-month') {
      priLabel = `${longMonth(priStart)} ${priStart.getFullYear()}`;
    } else if (preset === 'ytd') {
      priLabel = `${priStart.getFullYear()} YTD (same days)`;
    } else if (preset === 'this-week') {
      priLabel = `Week of ${fmtShort(priStart)}`;
    } else if (preset === 'last-12-months') {
      priLabel = 'Prior 12 Months';
    }
  }

  return {
    current: { start: isoDate(curStart), end: isoDate(curEnd), label: curLabel },
    prior: { start: isoDate(priStart), end: isoDate(priEnd), label: priLabel },
  };
}

// Generate ordered bucket-key list spanning [start, end] at the given granularity.
function generateBuckets(startIso, endIso, granularity) {
  const start = parseIso(startIso);
  const end = parseIso(endIso);
  const out = [];
  if (granularity === 'month') {
    const d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (d <= end) {
      out.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
      d.setMonth(d.getMonth() + 1);
    }
  } else if (granularity === 'week') {
    // Postgres ISO week: IYYY-"W"IW
    const d = new Date(start);
    while (d <= end) {
      out.push(isoWeekKey(d));
      d.setDate(d.getDate() + 7);
    }
  } else {
    const d = new Date(start);
    while (d <= end) {
      out.push(isoDate(d));
      d.setDate(d.getDate() + 1);
    }
  }
  return [...new Set(out)];
}

function isoWeekKey(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${pad(week)}`;
}

// ── Color helpers ─────────────────────────────────────────────────────────────
const NAVY = '#00395C';
const ORANGE = '#DC582A';
const GREEN = '#1f9d55';
const RED = '#c0392b';
const GRAY = '#9aa0a6';

const deltaColor = (p, isNew) => {
  if (isNew) return ORANGE;
  if (p === null || p === undefined) return GRAY;
  if (p > 0) return GREEN;
  if (p < 0) return RED;
  return GRAY;
};

// ── UI: Sparkline ─────────────────────────────────────────────────────────────
function Sparkline({ current, prior, width = 200, height = 40 }) {
  // current and prior are arrays of revenue values aligned positionally.
  if (!current || current.length === 0) return null;
  const len = Math.max(current.length, prior.length);
  const max = Math.max(...current, ...prior, 1);
  const x = (i) => (len <= 1 ? width / 2 : (i / (len - 1)) * (width - 2) + 1);
  const y = (v) => height - 2 - (v / max) * (height - 4);

  const path = (arr) => arr.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'block', overflow: 'visible' }}>
      {prior.length > 1 && <path d={path(prior)} fill="none" stroke={GRAY} strokeWidth={1.5} strokeDasharray="3 3" />}
      {current.length > 1 && <path d={path(current)} fill="none" stroke={ORANGE} strokeWidth={2} />}
      {len === 1 && (
        <>
          <circle cx={width * 0.35} cy={y(prior[0] ?? 0)} r={4} fill={GRAY} />
          <circle cx={width * 0.65} cy={y(current[0] ?? 0)} r={4} fill={ORANGE} />
          <text x={width * 0.35} y={height - 1} textAnchor="middle" fontSize={8} fill={GRAY}>prior</text>
          <text x={width * 0.65} y={height - 1} textAnchor="middle" fontSize={8} fill={ORANGE}>now</text>
        </>
      )}
    </svg>
  );
}

// ── UI: Vertical bars (current vs prior, one pair per category) ──────────────
// Round the axis top up to a clean tick so gridlines land on readable numbers.
function niceScale(max) {
  if (!(max > 0)) return { top: 1, step: 1 };
  const rough = max / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(rough)));
  const n = rough / mag;
  const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * mag;
  return { top: Math.ceil(max / step) * step, step };
}

function VerticalBars({ categories, onSelect, metric = 'revenue', currentLabel, priorLabel }) {
  const isOrders = metric === 'orders';
  const valOf = (side) => (isOrders ? side.orders : side.revenue);
  const fmtVal = isOrders ? (n) => Number(n).toLocaleString() : (n) => fmt$(n, { compact: true });

  const sorted = useMemo(() => {
    return [...categories]
      .filter(c => c.current.revenue > 0 || c.prior.revenue > 0)
      .sort((a, b) => (isOrders ? b.current.orders - a.current.orders : b.current.revenue - a.current.revenue));
  }, [categories, isOrders]);

  // Hard Crabs dwarfs every other category, so it gets its own chart on top
  // and the rest share a scale they can actually be read on.
  const hero = sorted.find(c => /^hard crabs$/i.test(c.name));
  const rest = hero ? sorted.filter(c => c !== hero) : sorted;

  const max = rest.length ? Math.max(...rest.map(c => Math.max(valOf(c.current), valOf(c.prior)))) : 1;
  const { top, step } = niceScale(max);
  const ticks = [];
  for (let t = 0; t <= top + step / 2; t += step) ticks.push(t);

  const PLOT_H = 340;
  const HEADROOM = 22; // room above the tallest bar for its value label
  const pctOf = (v) => (v / top) * 100;

  const fmtFull = isOrders ? (n) => Number(n).toLocaleString() : (n) => fmt$(n);
  const heroCur = hero ? valOf(hero.current) : 0;
  const heroPri = hero ? valOf(hero.prior) : 0;
  const heroMax = Math.max(heroCur, heroPri, 1);
  const heroPct = hero ? (isOrders ? hero.deltaOrdersPct : hero.deltaPct) : null;
  const heroDelta = heroCur - heroPri;

  return (
    <>
    {hero && (
      <button
        onClick={() => onSelect && onSelect(hero.name)}
        title="Click to drill into products"
        style={{ display: 'block', width: '100%', textAlign: 'left', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16, marginBottom: 12, cursor: 'pointer', fontFamily: 'inherit' }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>{hero.name}</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: deltaColor(heroPct, heroPct === null) }}>
            {heroPct === null ? 'NEW' : fmtPct(heroPct)}
          </span>
          <span style={{ fontSize: 12, color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
            {heroDelta >= 0 ? '+' : '-'}{fmtFull(Math.abs(heroDelta))} vs last period
          </span>
        </div>
        {[
          { label: currentLabel, v: heroCur, color: ORANGE, strong: true },
          { label: priorLabel, v: heroPri, color: GRAY, strong: false },
        ].map((row) => (
          <div key={row.label} style={{ display: 'grid', gridTemplateColumns: '150px 1fr 110px', alignItems: 'center', gap: 12, marginTop: 6 }}>
            <div style={{ fontSize: 12, color: 'var(--gray-700)', fontWeight: row.strong ? 700 : 500 }}>{row.label}</div>
            <div style={{ height: 28, background: 'var(--gray-50)', borderRadius: 3 }}>
              <div style={{ height: '100%', width: `${(row.v / heroMax) * 100}%`, background: row.color, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 15, fontWeight: row.strong ? 700 : 500, color: row.strong ? 'var(--navy)' : 'var(--gray-700)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtFull(row.v)}</div>
          </div>
        ))}
      </button>
    )}
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: 16 }}>
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--gray-700)', marginBottom: 4, alignItems: 'center' }}>
        {hero && <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>All other categories</span>}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, background: ORANGE, borderRadius: 2 }} /> {currentLabel}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          <span style={{ width: 10, height: 10, background: GRAY, borderRadius: 2 }} /> {priorLabel}
        </span>
      </div>
      <div style={{ display: 'flex' }}>
        <div style={{ position: 'relative', width: 52, flex: '0 0 52px', height: PLOT_H, marginTop: HEADROOM }}>
          {ticks.map((t) => (
            <div key={t} style={{ position: 'absolute', right: 8, bottom: `${pctOf(t)}%`, transform: 'translateY(50%)', fontSize: 11, color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
              {fmtVal(t)}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0, overflowX: 'auto' }}>
          <div style={{ position: 'relative', minWidth: rest.length * 62 }}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: HEADROOM, height: PLOT_H, pointerEvents: 'none' }}>
              {ticks.map((t) => (
                <div key={t} style={{ position: 'absolute', left: 0, right: 0, bottom: `${pctOf(t)}%`, height: 1, background: t === 0 ? 'var(--gray-300)' : 'var(--gray-100)' }} />
              ))}
            </div>
            <div style={{ display: 'flex', position: 'relative' }}>
              {rest.map((cat) => {
                const cur = valOf(cat.current);
                const pri = valOf(cat.prior);
                const pct = isOrders ? cat.deltaOrdersPct : cat.deltaPct;
                const isNew = pct === null;
                const color = deltaColor(pct, isNew);
                return (
                  <button
                    key={cat.name}
                    onClick={() => onSelect && onSelect(cat.name)}
                    title={`${cat.name}: ${fmtVal(cur)} vs ${fmtVal(pri)}`}
                    style={{ flex: '1 1 0', minWidth: 0, padding: '0 2px 6px', background: 'transparent', border: 'none', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(0,57,92,0.04)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ position: 'relative', height: PLOT_H, marginTop: HEADROOM, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 3 }}>
                      <div style={{ position: 'absolute', left: 0, right: 0, bottom: `calc(${pctOf(Math.max(cur, pri))}% + 4px)`, textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--navy)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {fmtVal(cur)}
                      </div>
                      <div style={{ width: '34%', maxWidth: 30, height: `${pctOf(cur)}%`, minHeight: cur > 0 ? 2 : 0, background: ORANGE, borderRadius: '3px 3px 0 0' }} />
                      <div style={{ width: '34%', maxWidth: 30, height: `${pctOf(pri)}%`, minHeight: pri > 0 ? 2 : 0, background: GRAY, borderRadius: '3px 3px 0 0' }} />
                    </div>
                    <div style={{ marginTop: 6, fontSize: 11, fontWeight: 600, color: 'var(--navy)', lineHeight: 1.2, minHeight: 27 }}>{cat.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color }}>{isNew ? 'NEW' : fmtPct(pct)}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>vs {fmtVal(pri)}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

// ── UI: Diverging bars (% delta) ─────────────────────────────────────────────
function DivergingBars({ categories, onSelect, metric = 'revenue' }) {
  // Sort: NEW (no prior) at top, then by deltaPct desc, then dropped (-100) at bottom.
  const sorted = useMemo(() => {
    const items = categories.filter(c => c.current.revenue > 0 || c.prior.revenue > 0);
    return [...items].sort((a, b) => {
      const aP = a.deltaPct, bP = b.deltaPct;
      if (aP === null && bP === null) return b.current.revenue - a.current.revenue;
      if (aP === null) return -1;
      if (bP === null) return 1;
      return bP - aP;
    });
  }, [categories]);

  // Cap visual bar width at 100% delta (positive or negative) so wild outliers don't break the scale.
  const VISUAL_CAP = 100;
  const halfTrack = 50; // % of row width on each side of zero
  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '8px 0' }}>
      {sorted.map((cat) => {
        const pct = cat.deltaPct;
        const isNew = pct === null;
        const visualPct = isNew ? VISUAL_CAP : Math.max(-VISUAL_CAP, Math.min(VISUAL_CAP, pct));
        const barWidth = Math.abs(visualPct) / VISUAL_CAP * halfTrack;
        const color = deltaColor(pct, isNew);
        const cur = metric === 'orders' ? cat.current.orders : cat.current.revenue;
        const pri = metric === 'orders' ? cat.prior.orders : cat.prior.revenue;
        const fmtVal = metric === 'orders' ? (n) => Number(n).toLocaleString() : (n) => fmt$(n, { compact: true });
        return (
          <button
            key={cat.name}
            onClick={() => onSelect && onSelect(cat.name)}
            style={{
              display: 'grid',
              gridTemplateColumns: '160px 1fr 70px 180px',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '6px 16px',
              background: 'transparent',
              border: 'none',
              borderTop: '1px solid var(--gray-100)',
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'var(--gray-50)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
          >
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cat.name}
            </div>
            <div style={{ position: 'relative', height: 22, background: 'var(--gray-50)', borderRadius: 3 }}>
              <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 1, background: 'var(--gray-300)' }} />
              {!isNew && pct < 0 && (
                <div style={{
                  position: 'absolute',
                  right: '50%',
                  top: 3,
                  bottom: 3,
                  width: `${barWidth}%`,
                  background: color,
                  borderRadius: '3px 0 0 3px',
                }} />
              )}
              {(!isNew && pct > 0) && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  top: 3,
                  bottom: 3,
                  width: `${barWidth}%`,
                  background: color,
                  borderRadius: '0 3px 3px 0',
                }} />
              )}
              {isNew && (
                <div style={{
                  position: 'absolute',
                  left: '50%',
                  top: 3,
                  bottom: 3,
                  width: '50%',
                  background: 'repeating-linear-gradient(45deg, #DC582A, #DC582A 4px, #f4ad8e 4px, #f4ad8e 8px)',
                  borderRadius: '0 3px 3px 0',
                }} />
              )}
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color, textAlign: 'right' }}>
              {isNew ? 'NEW' : fmtPct(pct)}
            </div>
            <div style={{ fontSize: 11, color: 'var(--gray-700)', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              <strong style={{ color: 'var(--navy)' }}>{fmtVal(cur)}</strong>
              <span style={{ color: 'var(--gray-500)' }}> vs {fmtVal(pri)}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── UI: Small multiples grid ────────────────────────────────────────────────
function SmallMultiples({ categories, onSelect, currentBuckets, priorBuckets, granularity, metric = 'revenue' }) {
  const sorted = useMemo(() => {
    return [...categories]
      .filter(c => c.current.revenue > 0 || c.prior.revenue > 0)
      .sort((a, b) => b.current.revenue - a.current.revenue);
  }, [categories]);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
      {sorted.map((cat) => {
        const cur = metric === 'orders' ? cat.current.orders : cat.current.revenue;
        const pri = metric === 'orders' ? cat.prior.orders : cat.prior.revenue;
        const pct = metric === 'orders' ? cat.deltaOrdersPct : cat.deltaPct;
        const isNew = pct === null;
        const color = deltaColor(pct, isNew);

        const curSeries = currentBuckets.map(k => {
          const b = cat.current.buckets[k];
          return metric === 'orders' ? (b?.orders ?? 0) : (b?.revenue ?? 0);
        });
        const priSeries = priorBuckets.map(k => {
          const b = cat.prior.buckets[k];
          return metric === 'orders' ? (b?.orders ?? 0) : (b?.revenue ?? 0);
        });

        return (
          <button
            key={cat.name}
            onClick={() => onSelect && onSelect(cat.name)}
            className="card"
            style={{
              padding: 12,
              cursor: 'pointer',
              border: '1px solid var(--gray-200)',
              borderRadius: 8,
              background: '#fff',
              textAlign: 'left',
              fontFamily: 'inherit',
              transition: 'transform 0.1s, box-shadow 0.1s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,57,92,0.12)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>
                {cat.name}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: 2 }}>
                {!isNew && pct > 0 && <ArrowUp size={12} />}
                {!isNew && pct < 0 && <ArrowDown size={12} />}
                {isNew ? 'NEW' : fmtPct(pct)}
              </div>
            </div>
            <Sparkline current={curSeries} prior={priSeries} width={196} height={36} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
              <span style={{ color: 'var(--gray-500)' }}>
                Prior: <strong style={{ color: 'var(--gray-700)' }}>{metric === 'orders' ? Number(pri).toLocaleString() : fmt$(pri, { compact: true })}</strong>
              </span>
              <span style={{ color: 'var(--gray-500)' }}>
                Now: <strong style={{ color: 'var(--orange)' }}>{metric === 'orders' ? Number(cur).toLocaleString() : fmt$(cur, { compact: true })}</strong>
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ── UI: Absolute $ stack-rank bars ───────────────────────────────────────────
function AbsoluteBars({ categories, metric = 'revenue' }) {
  const sorted = useMemo(() => {
    return [...categories]
      .filter(c => c.current.revenue > 0 || c.prior.revenue > 0)
      .sort((a, b) => {
        const av = metric === 'orders' ? a.current.orders : a.current.revenue;
        const bv = metric === 'orders' ? b.current.orders : b.current.revenue;
        return bv - av;
      });
  }, [categories, metric]);
  const max = sorted.length ? Math.max(...sorted.map(c => metric === 'orders' ? Math.max(c.current.orders, c.prior.orders) : Math.max(c.current.revenue, c.prior.revenue))) : 1;

  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '8px 0' }}>
      {sorted.map((cat) => {
        const cur = metric === 'orders' ? cat.current.orders : cat.current.revenue;
        const pri = metric === 'orders' ? cat.prior.orders : cat.prior.revenue;
        const fmtVal = metric === 'orders' ? (n) => Number(n).toLocaleString() : (n) => fmt$(n, { compact: true });
        return (
          <div key={cat.name} style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr 110px',
            alignItems: 'center',
            gap: 12,
            padding: '8px 16px',
            borderTop: '1px solid var(--gray-100)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {cat.name}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ height: 12, background: 'var(--gray-50)', borderRadius: 2, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(pri / max) * 100}%`, background: GRAY, borderRadius: 2 }} />
              </div>
              <div style={{ height: 12, background: 'var(--gray-50)', borderRadius: 2, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${(cur / max) * 100}%`, background: ORANGE, borderRadius: 2 }} />
              </div>
            </div>
            <div style={{ fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              <div style={{ color: 'var(--gray-500)' }}>Prior: {fmtVal(pri)}</div>
              <div style={{ color: 'var(--orange)', fontWeight: 700 }}>Now: {fmtVal(cur)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── UI: Share-of-mix ─────────────────────────────────────────────────────────
function ShareOfMix({ categories }) {
  const totalCur = categories.reduce((s, c) => s + c.current.revenue, 0) || 1;
  const totalPri = categories.reduce((s, c) => s + c.prior.revenue, 0) || 1;
  const sorted = [...categories]
    .filter(c => c.current.revenue > 0 || c.prior.revenue > 0)
    .sort((a, b) => b.current.revenue - a.current.revenue);

  return (
    <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8, padding: '8px 0' }}>
      {sorted.map((cat) => {
        const curShare = (cat.current.revenue / totalCur) * 100;
        const priShare = (cat.prior.revenue / totalPri) * 100;
        const delta = curShare - priShare;
        const color = delta > 0.5 ? GREEN : delta < -0.5 ? RED : GRAY;
        return (
          <div key={cat.name} style={{
            display: 'grid',
            gridTemplateColumns: '160px 1fr 90px',
            alignItems: 'center',
            gap: 12,
            padding: '8px 16px',
            borderTop: '1px solid var(--gray-100)',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.name}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <div style={{ height: 10, background: 'var(--gray-50)', borderRadius: 2, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${priShare}%`, background: GRAY, borderRadius: 2 }} />
              </div>
              <div style={{ height: 10, background: 'var(--gray-50)', borderRadius: 2, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${curShare}%`, background: ORANGE, borderRadius: 2 }} />
              </div>
            </div>
            <div style={{ fontSize: 11, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
              <div style={{ color: 'var(--gray-500)' }}>Was {priShare.toFixed(1)}%</div>
              <div style={{ color: 'var(--orange)', fontWeight: 700 }}>
                Now {curShare.toFixed(1)}% <span style={{ color, fontWeight: 600 }}>({delta > 0 ? '+' : ''}{delta.toFixed(1)} pp)</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── UI: Drill-in panel ───────────────────────────────────────────────────────
function DrillIn({ categoryName, current, prior, onClose }) {
  const [products, setProducts] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setProducts(null);
    setError(null);
    fetchCategorySalesProducts({ categoryName, current, prior })
      .then(setProducts)
      .catch(e => setError(e.message));
  }, [categoryName, current.start, current.end, prior.start, prior.end]);

  return (
    <div style={{
      background: '#fff',
      border: '1px solid var(--navy)',
      borderRadius: 8,
      marginTop: 16,
      padding: 16,
      boxShadow: '0 4px 16px rgba(0,57,92,0.08)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={onClose}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: 11, color: 'var(--gray-700)', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            <ChevronLeft size={12} /> Close
          </button>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{categoryName} — product breakdown</div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{current.label} <span style={{ color: 'var(--gray-300)' }}>·</span> {prior.label}</div>
      </div>

      {error && <div style={{ fontSize: 13, color: RED, padding: 12 }}>Error: {error}</div>}
      {!products && !error && <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 12 }}>Loading products…</div>}

      {products && products.length === 0 && (
        <div style={{ fontSize: 12, color: 'var(--gray-500)', padding: 12 }}>No products in this category for the selected period.</div>
      )}

      {products && products.length > 0 && (() => {
        const tot = products.reduce((s, p) => ({
          curRev: s.curRev + p.current.revenue,
          curOrders: s.curOrders + p.current.orders,
          curUnits: s.curUnits + p.current.units,
          priorRev: s.priorRev + p.prior.revenue,
          priorOrders: s.priorOrders + p.prior.orders,
          priorUnits: s.priorUnits + p.prior.units,
        }), { curRev: 0, curOrders: 0, curUnits: 0, priorRev: 0, priorOrders: 0, priorUnits: 0 });
        const totDeltaRev = tot.curRev - tot.priorRev;
        const totDeltaPct = tot.priorRev > 0 ? (totDeltaRev / tot.priorRev) * 100 : null;
        return (
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', fontVariantNumeric: 'tabular-nums' }}>
          <thead>
            <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
              <th rowSpan={2} style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--gray-700)', fontWeight: 700, verticalAlign: 'bottom' }}>Product</th>
              <th colSpan={3} style={{ textAlign: 'center', padding: '4px 8px 2px', color: 'var(--orange)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--gray-200)' }}>Now</th>
              <th colSpan={3} style={{ textAlign: 'center', padding: '4px 8px 2px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--gray-200)' }}>Prior</th>
              <th colSpan={2} style={{ textAlign: 'center', padding: '4px 8px 2px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--gray-200)' }}>Δ</th>
            </tr>
            <tr style={{ background: 'var(--gray-50)' }}>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11 }}>$</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11 }}>Orders</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11, borderRight: '1px solid var(--gray-200)' }}>Units</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11 }}>$</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11 }}>Orders</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11, borderRight: '1px solid var(--gray-200)' }}>Units</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11 }}>$</th>
              <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--gray-700)', fontWeight: 700, fontSize: 11 }}>%</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const isNew = p.deltaPct === null;
              const color = deltaColor(p.deltaPct, isNew);
              return (
                <tr key={p.name} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '6px 8px', color: 'var(--navy)' }}>{p.name}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--orange)', fontWeight: 600 }}>{fmt$(p.current.revenue)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-700)' }}>{Number(p.current.orders).toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-700)', borderRight: '1px solid var(--gray-100)' }}>{Number(p.current.units).toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{fmt$(p.prior.revenue)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-500)' }}>{Number(p.prior.orders).toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: 'var(--gray-500)', borderRight: '1px solid var(--gray-100)' }}>{Number(p.prior.units).toLocaleString()}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color }}>{p.deltaRev >= 0 ? '+' : ''}{fmt$(p.deltaRev)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color, fontWeight: 700 }}>{isNew ? 'NEW' : fmtPct(p.deltaPct)}</td>
                </tr>
              );
            })}
            <tr style={{ background: 'var(--gray-50)', borderTop: '2px solid var(--gray-200)', fontWeight: 700 }}>
              <td style={{ padding: '8px', color: 'var(--navy)' }}>Total ({products.length} products)</td>
              <td style={{ padding: '8px', textAlign: 'right', color: 'var(--orange)' }}>{fmt$(tot.curRev)}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: 'var(--navy)' }}>{Number(tot.curOrders).toLocaleString()}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: 'var(--navy)', borderRight: '1px solid var(--gray-100)' }}>{Number(tot.curUnits).toLocaleString()}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: 'var(--gray-700)' }}>{fmt$(tot.priorRev)}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: 'var(--gray-700)' }}>{Number(tot.priorOrders).toLocaleString()}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: 'var(--gray-700)', borderRight: '1px solid var(--gray-100)' }}>{Number(tot.priorUnits).toLocaleString()}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: deltaColor(totDeltaPct, false) }}>{totDeltaRev >= 0 ? '+' : ''}{fmt$(totDeltaRev)}</td>
              <td style={{ padding: '8px', textAlign: 'right', color: deltaColor(totDeltaPct, false) }}>{fmtPct(totDeltaPct)}</td>
            </tr>
          </tbody>
        </table>
        );
      })()}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
const PRESETS = [
  { id: 'mtd', label: 'Month to date' },
  { id: 'last-month', label: 'Last full month' },
  { id: 'ytd', label: 'Year to date' },
  { id: 'this-week', label: 'This week' },
  { id: 'last-12-months', label: 'Last 12 months' },
  { id: 'custom', label: 'Custom range' },
];

const VIEWS = [
  { id: 'bars', label: 'Bars' },
  { id: 'pct', label: '% Change' },
  { id: 'absolute', label: 'Absolute $' },
  { id: 'mix', label: 'Share of Mix' },
];

const GRAINS = [
  { id: 'month', label: 'Month' },
  { id: 'week', label: 'Week' },
  { id: 'day', label: 'Day' },
];

export default function CategorySalesReport() {
  const [comparison, setComparison] = useState('yoy');
  const [preset, setPreset] = useState('ytd');
  const [granularity, setGranularity] = useState('month');
  const [view, setView] = useState('bars');
  const [metric, setMetric] = useState('revenue');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [drilledCategory, setDrilledCategory] = useState(null);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const range = useMemo(() => buildRange(preset, comparison, customStart, customEnd), [preset, comparison, customStart, customEnd]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchCategorySalesReport({ current: range.current, prior: range.prior, granularity })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [range, granularity]);

  useEffect(() => { load(); }, [load, refreshTick]);

  const currentBuckets = useMemo(() => generateBuckets(range.current.start, range.current.end, granularity), [range, granularity]);
  const priorBuckets = useMemo(() => generateBuckets(range.prior.start, range.prior.end, granularity), [range, granularity]);

  // Reset drill-in when range/granularity changes
  useEffect(() => { setDrilledCategory(null); }, [range.current.start, range.current.end, range.prior.start, range.prior.end]);

  const totals = data?.totals;
  const revenuePctColor = totals?.revenueDeltaPct == null ? GRAY : (totals.revenueDeltaPct > 0 ? GREEN : (totals.revenueDeltaPct < 0 ? RED : GRAY));
  const ordersPctColor = totals?.ordersDeltaPct == null ? GRAY : (totals.ordersDeltaPct > 0 ? GREEN : (totals.ordersDeltaPct < 0 ? RED : GRAY));

  return (
    <div>
      <div className="breadcrumb">
        <span>Dashboard</span>
        <span> › </span>
        <span>Reports</span>
        <span> › </span>
        <span>Category Sales</span>
      </div>
      <div className="page-header">Category Sales</div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'flex-end',
        background: '#fff',
        border: '1px solid var(--gray-200)',
        borderRadius: 8,
        padding: 12,
        marginTop: 12,
      }}>
        <Field label="Compare">
          <SegSelect value={comparison} onChange={setComparison} options={[{ id: 'yoy', label: 'YoY' }, { id: 'mom', label: 'MoM' }]} />
        </Field>
        <Field label="Range">
          <select value={preset} onChange={e => setPreset(e.target.value)} style={selectStyle}>
            {PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </Field>
        {preset === 'custom' && (
          <>
            <Field label="From">
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} style={inputStyle} />
            </Field>
            <Field label="To">
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} style={inputStyle} />
            </Field>
          </>
        )}
        <Field label="Granularity">
          <SegSelect value={granularity} onChange={setGranularity} options={GRAINS} />
        </Field>
        <Field label="View">
          <SegSelect value={view} onChange={setView} options={VIEWS} />
        </Field>
        <Field label="Metric">
          <SegSelect value={metric} onChange={setMetric} options={[{ id: 'revenue', label: '$' }, { id: 'orders', label: 'Orders' }]} />
        </Field>
        <button
          onClick={() => setRefreshTick(t => t + 1)}
          title="Refresh"
          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', fontSize: 11, color: 'var(--navy)', background: 'var(--gray-50)', border: '1px solid var(--gray-200)', borderRadius: 4, cursor: 'pointer', height: 32, fontFamily: 'inherit' }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Summary tile */}
      <div style={{
        marginTop: 12,
        background: 'linear-gradient(135deg, #00395C 0%, #005785 100%)',
        color: '#fff',
        borderRadius: 8,
        padding: '14px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Period</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>{range.current.label}</div>
          <div style={{ fontSize: 11, opacity: 0.85, marginTop: 2 }}>vs {range.prior.label}</div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 4 }}>
            {fmtShort(parseIso(range.current.start))} – {fmtShort(parseIso(range.current.end))}
            <br />vs {fmtShort(parseIso(range.prior.start))} – {fmtShort(parseIso(range.prior.end))}
          </div>
        </div>
        <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.2)' }} />
        <div>
          <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Revenue</div>
          {!totals ? (
            <div style={{ fontSize: 13, opacity: 0.85 }}>{loading ? 'Loading…' : (error ? 'Error' : '—')}</div>
          ) : (
            <>
              <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt$(totals.currentRevenue)}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: revenuePctColor === GREEN ? '#7fffa9' : revenuePctColor === RED ? '#ffb0a8' : 'rgba(255,255,255,0.7)' }}>
                {totals.revenueDeltaPct == null ? '—' : (
                  <>
                    {totals.revenueDeltaPct > 0 ? '↑' : totals.revenueDeltaPct < 0 ? '↓' : '→'} {fmtPct(totals.revenueDeltaPct)}
                    <span style={{ opacity: 0.7, marginLeft: 6 }}>({fmt$(totals.priorRevenue)} prior)</span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
        <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.2)' }} />
        <div>
          <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>Orders</div>
          {!totals ? (
            <div style={{ fontSize: 13, opacity: 0.85 }}>{loading ? 'Loading…' : (error ? 'Error' : '—')}</div>
          ) : (
            <>
              <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{Number(totals.currentOrders).toLocaleString()}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: ordersPctColor === GREEN ? '#7fffa9' : ordersPctColor === RED ? '#ffb0a8' : 'rgba(255,255,255,0.7)' }}>
                {totals.ordersDeltaPct == null ? '—' : (
                  <>
                    {totals.ordersDeltaPct > 0 ? '↑' : totals.ordersDeltaPct < 0 ? '↓' : '→'} {fmtPct(totals.ordersDeltaPct)}
                    <span style={{ opacity: 0.7, marginLeft: 6 }}>({Number(totals.priorOrders).toLocaleString()} prior)</span>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {error && (
        <div style={{ marginTop: 16, padding: 14, background: '#fff5f5', border: '1px solid #fcc', borderRadius: 6, fontSize: 13, color: RED }}>
          <strong>Error loading category data:</strong> {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ marginTop: 16, padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--gray-500)' }}>
          Loading category sales…
        </div>
      )}

      {/* Sections */}
      {data && !error && (
        <>
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <span>
                {view === 'bars' && <>{metric === 'orders' ? 'Orders' : 'Revenue'} by category <span style={{ color: 'var(--gray-500)', fontWeight: 400, fontSize: 11 }}>· {range.current.label} vs {range.prior.label} · biggest first · click a category to drill into products</span></>}
                {view === 'pct' && <>Year-over-year delta by category <span style={{ color: 'var(--gray-500)', fontWeight: 400, fontSize: 11 }}>· sorted biggest gainer to biggest loser · click a row to drill into products</span></>}
                {view === 'absolute' && <>Absolute {metric === 'orders' ? 'orders' : 'revenue'} by category <span style={{ color: 'var(--gray-500)', fontWeight: 400, fontSize: 11 }}>· sorted by current period</span></>}
                {view === 'mix' && <>Share of total revenue <span style={{ color: 'var(--gray-500)', fontWeight: 400, fontSize: 11 }}>· each category's % of total, current vs prior</span></>}
              </span>
              {totals && (
                <span style={{ fontSize: 10, color: 'var(--gray-500)', fontWeight: 500 }}>
                  Categories total {fmt$(totals.currentLineItemRevenue)} vs {fmt$(totals.priorLineItemRevenue)}
                  <span style={{ color: 'var(--gray-300)', margin: '0 4px' }}>·</span>
                  what customers paid for product, after VIP and coupon discounts · headline also includes shipping
                </span>
              )}
            </div>
            {view === 'bars' && <VerticalBars categories={data.categories} onSelect={setDrilledCategory} metric={metric} currentLabel={range.current.label} priorLabel={range.prior.label} />}
            {view === 'pct' && <DivergingBars categories={data.categories} onSelect={setDrilledCategory} metric={metric} />}
            {view === 'absolute' && <AbsoluteBars categories={data.categories} metric={metric} />}
            {view === 'mix' && <ShareOfMix categories={data.categories} />}
          </div>

          {drilledCategory && (
            <DrillIn
              categoryName={drilledCategory}
              current={range.current}
              prior={range.prior}
              onClose={() => setDrilledCategory(null)}
            />
          )}

          <div style={{ marginTop: 24, display: view === 'bars' ? 'none' : undefined }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
              Per-category trend
              <span style={{ color: 'var(--gray-500)', fontWeight: 400, fontSize: 11 }}>
                {' '}· each category gets the same screen real estate · solid orange = current, dashed gray = prior · click to drill in
              </span>
            </div>
            <SmallMultiples
              categories={data.categories}
              onSelect={setDrilledCategory}
              currentBuckets={currentBuckets}
              priorBuckets={priorBuckets}
              granularity={granularity}
              metric={metric}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ── Tiny inline UI primitives ───────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
      {children}
    </div>
  );
}

const selectStyle = { fontFamily: 'inherit', fontSize: 12, padding: '6px 8px', border: '1px solid var(--gray-200)', borderRadius: 4, background: '#fff', color: 'var(--navy)', height: 32 };
const inputStyle = { ...selectStyle };

function SegSelect({ value, onChange, options }) {
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--gray-200)', borderRadius: 4, overflow: 'hidden', height: 32 }}>
      {options.map((opt) => {
        const active = opt.id === value;
        return (
          <button
            key={opt.id}
            onClick={() => onChange(opt.id)}
            style={{
              padding: '0 12px',
              fontSize: 11,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              background: active ? 'var(--navy)' : '#fff',
              color: active ? '#fff' : 'var(--navy)',
              fontFamily: 'inherit',
              borderRight: '1px solid var(--gray-200)',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
